import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import TableIdButton from "@lib/components/MiniComponents/Buttons/TableIdButton";
import { shortenString } from "@lib/helpers/StringHelper";
import { ColumnConfig, generateColumns } from "@lib/helpers/TableHelper";
import { TableColumnsProps } from "@lib/types/General";
import { IntlShape } from "react-intl";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { LanguageLabel } from "@lib/components/MiniComponents/Others";
import { Tag } from "antd";

import {
  FRAUD_STATUS_COLOR,
  MarketPlaceFraudDetection,
} from "@lib/types/FraudDetection";
import { DATE_FORMATS } from "@lib/constants/date";

type Props<T> = TableColumnsProps<T> & {
  handleSelectItem: (item: T) => void;
  messages: IntlShape["messages"];
};

const getUserName = (record: MarketPlaceFraudDetection) => {
  const { firstName, lastName } = record.requestData?.user ?? {};
  const name = [firstName, lastName].filter(Boolean).join(" ").trim();
  return name || "-";
};

const getColumns = <T extends MarketPlaceFraudDetection>(
  props: Props<T>,
): ColumnsType<T> => {
  const { handleSelectItem, messages } = props;

  const columnsConfig: ColumnConfig<T>[] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      dontSort: true,
      filterType: undefined,
      width: "180px",
      render: (_, r) => (
        <TableIdButton text={`#${shortenString(r.id)}`} textToBeCopied={r.id} />
      ),
    },
    {
      title: "common.status",
      dataIndex: "status",
      key: "status",
      width: "120px",
      filterType: "dropdown",
      render: (status: MarketPlaceFraudDetection["status"]) =>
        status ? (
          <Tag color={FRAUD_STATUS_COLOR[status] ?? "default"}>{status}</Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "common.score",
      dataIndex: "score",
      key: "score",
      width: "100px",
      filterType: "numberRange",
      render: (score: MarketPlaceFraudDetection["score"]) =>
        (score ?? score === 0) ? String(score) : "-",
    },
    {
      title: "common.decision",
      dataIndex: "decision",
      key: "decision",
      width: "140px",
      filterType: "dropdown",
      render: (decision: MarketPlaceFraudDetection["decision"]) =>
        decision || "-",
    },
    {
      title: "common.summary",
      dataIndex: "summary",
      key: "summary",
      width: "220px",
      filterType: "search",
      render: (summary: MarketPlaceFraudDetection["summary"]) => (
        <div
          style={{
            maxWidth: "220px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {summary || "-"}
        </div>
      ),
    },
    {
      title: "common.error",
      dataIndex: "error",
      key: "error",
      width: "180px",
      filterType: "search",
      render: (error: MarketPlaceFraudDetection["error"]) => error || "-",
    },
    {
      title: "common.name",
      dataIndex: "requestData",
      key: "name",
      width: "150px",
      dontSort: true,
      filterType: undefined,
      render: (_, record) => getUserName(record),
    },
    {
      title: "common.email",
      dataIndex: "requestData",
      key: "email",
      width: "180px",
      dontSort: true,
      filterType: undefined,
      render: (_, record) => record.requestData?.user?.email || "-",
    },
    {
      title: "common.phone",
      dataIndex: "requestData",
      key: "phone",
      width: "150px",
      dontSort: true,
      filterType: undefined,
      render: (_, record) => record.requestData?.user?.phone || "-",
    },
    {
      title: "common.country",
      dataIndex: "requestData",
      key: "country",
      width: "110px",
      dontSort: true,
      filterType: undefined,
      render: (_, record) => record.requestData?.user?.country || "-",
    },
    {
      title: "common.language",
      dataIndex: "requestData",
      key: "language",
      width: "120px",
      dontSort: true,
      filterType: undefined,
      render: (_, record) => {
        const language = record.requestData?.user?.language;
        return language ? <LanguageLabel languageCode={language} /> : "-";
      },
    },
    {
      title: "common.isAdvanced",
      dataIndex: "requestData",
      key: "isAdvanced",
      width: "120px",
      dontSort: true,
      filterType: undefined,
      render: (_, record) =>
        record.requestData?.isAdvanced
          ? (messages["common.yes"] as string)
          : (messages["common.no"] as string),
    },
    {
      title: "common.userId",
      dataIndex: "userId",
      key: "userId",
      width: "180px",
      filterType: "search",
      render: (userId: MarketPlaceFraudDetection["userId"]) => (
        <TableIdButton
          text={`#${shortenString(userId)}`}
          textToBeCopied={userId}
        />
      ),
    },
    {
      title: "common.customerId",
      dataIndex: "customerId",
      key: "customerId",
      width: "180px",
      filterType: "search",
      render: (customerId: MarketPlaceFraudDetection["customerId"]) => (
        <TableIdButton
          text={`#${shortenString(customerId)}`}
          textToBeCopied={customerId}
        />
      ),
    },
    {
      title: "common.createdAt",
      dataIndex: "createdAt",
      key: "createdAt",
      width: "150px",
      render: (d) =>
        d ? dayjs(d).format(DATE_FORMATS.DAY_MONTH_YEAR_TIME) : "-",
    },
    {
      title: "common.updatedAt",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: "150px",
      render: (d) =>
        d ? dayjs(d).format(DATE_FORMATS.DAY_MONTH_YEAR_TIME) : "-",
    },
    {
      title: "common.actions",
      dataIndex: "actions",
      key: "actions",
      className: "order-table-action",
      fixed: "right",
      width: "110px",
      render: (_, record) => (
        <StyledFlex className="actions">
          <ActionButton
            type="quickLook"
            onClick={() => handleSelectItem(record)}
            noLabel
          />
        </StyledFlex>
      ),
    },
  ];

  return generateColumns(props, columnsConfig);
};

export default getColumns;
